import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSecurity } from '../security/SecurityContext';
import useProfileData from '../services/ProfileDataService';
import { fetchUsers } from '../services/usersService';
import { useDemoUser } from '../hooks/useDemoUser';
import { PageLayout } from './authentication/PageLayout';
import { resolveApiAssetUrl } from '../lib/resolveApiAssetUrl';
import AvatarImage from './AvatarImage';
import {
  fetchApiApplicationInfo,
  type ApplicationBuildInfo,
} from '../services/applicationInfoService';

export const NavMenu = () => {
  // const [collapsed, setCollapsed] = useState(true);
  const { userName, profileImage } = useProfileData();
  const { has, roles, loading, user } = useSecurity();
  const { demoModeAvailable, demoUserEmail, isUsingDemoUser, setDemoUserEmail, clearDemoUserEmail } = useDemoUser();
  const canManageUsers = has('Manage_Users');
  const canManageRoles = has('Manage_Roles');
  const [show, setShow] = useState(false);
  const [apiApplicationInfo, setApiApplicationInfo] = useState<ApplicationBuildInfo | null>();
  const [demoUserOptions, setDemoUserOptions] = useState<Array<{ id: string; name: string; emailAddress: string; avatarUri?: string | null }>>([]);
  const [demoUsersLoading, setDemoUsersLoading] = useState(false);
  const [demoUsersError, setDemoUsersError] = useState<string | null>(null);
  const [demoSelectorOpen, setDemoSelectorOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const defaultProfileImage = "/defaultProfilePicture.png";
  const displayName = user?.name?.trim() || userName || 'User';
  const displayAvatar =
    resolveApiAssetUrl(user?.avatarUri) ||
    resolveApiAssetUrl(profileImage) ||
    profileImage ||
    defaultProfileImage;
  const roleNames = Array.isArray(roles)
    ? roles.map(role => role?.name?.trim()).filter((roleName): roleName is string => Boolean(roleName))
        .sort((a, b) => {
          if (a.toLowerCase() === 'admin') return -1;
          if (b.toLowerCase() === 'admin') return 1;
          return 0;
        })
    : [];
  const visibleRoleNames = roleNames.slice(0, 2);
  const hiddenRoleCount = roleNames.length - visibleRoleNames.length;
  const emptyRolesText = loading ? 'Loading Roles...' : 'No roles assigned';
  const rolesText = roleNames.length > 0 ? roleNames.join(', ') : emptyRolesText;
  const apiApplicationLabel = apiApplicationInfo === undefined
    ? 'Application version loading'
    : apiApplicationInfo === null
      ? 'Application version unavailable'
      : `Application version v${apiApplicationInfo.applicationVersion}, build ${apiApplicationInfo.buildNumber}`;
  const selectedDemoUserOption = useMemo(
    () => demoUserOptions.find((option) => option.emailAddress === demoUserEmail) || null,
    [demoUserEmail, demoUserOptions]
  );

  const togglePopup = () => setShow(prev => !prev);
  // const toggleNavbar = () => setCollapsed(!collapsed);

  useEffect(() => {
    let active = true;

    void fetchApiApplicationInfo().then((applicationInfo) => {
      if (active) {
        setApiApplicationInfo(applicationInfo);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!show || !demoModeAvailable) return;

    let active = true;
    setDemoUsersLoading(true);
    setDemoUsersError(null);

    void (async () => {
      try {
        const response = await fetchUsers({
          page: 1,
          pageSize: 100,
          skip: 0,
          includeDeleted: false,
          orderByField: 'Name',
          descending: false,
        });
        if (!active) return;

        const nextOptions = Array.isArray(response.results)
          ? response.results
              .filter((item) => item?.emailAddress)
              .map((item) => ({
                id: item.id || item.userId || item.emailAddress,
                name: item.name || item.emailAddress,
                emailAddress: item.emailAddress,
                avatarUri: item.avatarUri ?? null,
              }))
          : [];

        setDemoUserOptions(nextOptions);
      } catch (_error) {
        if (!active) return;
        setDemoUsersError('Failed to load demo users.');
        setDemoUserOptions([]);
      } finally {
        if (active) {
          setDemoUsersLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [demoModeAvailable, show]);

  useEffect(() => {
    if (!show) {
      setDemoSelectorOpen(false);
    }
  }, [show]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        anchor.current && 
        !dropdownRef.current.contains(event.target as Node) && 
        !anchor.current.contains(event.target as Node)
      ) {
        setShow(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDemoUserChange = (nextValue: string) => {
    if (!nextValue) {
      clearDemoUserEmail();
      setDemoSelectorOpen(false);
      return;
    }

    setDemoUserEmail(nextValue);
    setDemoSelectorOpen(false);
  };

  return (
    <header data-pdf-app-header className="bg-white border-b border-gray-200">
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center space-x-2">
          <Link to="/"><img className="h-8" src="/logo.svg" alt="Daikin Logo" /></Link>
          <span className="mx-2 h-6 w-px bg-gray-300" aria-hidden="true"></span>
          <span className="text-lg text-[#838689]">Code Sheet</span>
        </div>

        {/* data-pdf-omit: dropped from the PDF header clone, which keeps only the branding. */}
        <div data-pdf-omit className="flex items-center gap-6">
          {/* Topbar navigation (right-aligned, before user menu) */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm text-[#1D5176] hover:text-gray-900 hover:underline">Codesheets</Link>
            <Link
              to={canManageUsers ? "/users" : "#"}
              onClick={(e) => { if (!canManageUsers) e.preventDefault(); }}
              className={`text-sm ${canManageUsers ? 'text-[#1D5176] hover:text-gray-900 hover:underline' : 'text-gray-400 cursor-not-allowed'}`}
              title={canManageUsers ? undefined : "You don't have permission to manage users"}
            >
              Users
            </Link>
            <Link
              to={canManageRoles ? "/roles" : "#"}
              onClick={(e) => { if (!canManageRoles) e.preventDefault(); }}
              className={`text-sm ${canManageRoles ? 'text-[#1D5176] hover:text-gray-900 hover:underline' : 'text-gray-400 cursor-not-allowed'}`}
              title={canManageRoles ? undefined : "You don't have permission to manage roles"}
            >
              Roles
            </Link>
          </nav>

          <div className="flex items-center">
            <div className="relative">
            <button 
              className="flex items-center space-x-2 hover:bg-gray-100 rounded-md px-2 py-1 focus:outline-none"
              onClick={togglePopup}
              ref={anchor}
              aria-expanded={show}
              aria-haspopup="true"
              aria-label={`${displayName} roles: ${rolesText}`}
            >
              <div className="hidden md:flex items-center justify-end gap-1.5 mr-2 max-w-[360px] overflow-hidden">
                <span className="max-w-[170px] truncate text-sm font-medium text-gray-700">{displayName}</span>
                <span className="flex min-w-0 items-center justify-end gap-1">
                  {visibleRoleNames.length > 0 ? (
                    <>
                      {visibleRoleNames.map(roleName => (
                        <span
                          key={roleName}
                          className="max-w-[110px] truncate rounded border border-[#C9DDF1] bg-[#E6F0FA] px-1.5 py-0.5 text-[10px] leading-none text-[#1D5176]"
                          title={roleName}
                        >
                          {roleName}
                        </span>
                      ))}
                      {hiddenRoleCount > 0 && (
                        <span
                          className="rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5 text-[10px] leading-none text-gray-700"
                          title={rolesText}
                          aria-label={`Additional roles: ${roleNames.slice(2).join(', ')}`}
                        >
                          +{hiddenRoleCount}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="whitespace-nowrap text-[10px] leading-none text-gray-500">{emptyRolesText}</span>
                  )}
                </span>
              </div>
              <img 
                className="w-8 h-8 rounded-full border border-gray-200"
                src={displayAvatar}
                alt="User profile"
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  (e.target as HTMLImageElement).src = defaultProfileImage;
                }}
              />
              <svg 
                className={`w-4 h-4 ml-1 text-gray-500 transition-transform duration-200 ${show ? 'rotate-180' : ''}`}
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </button>

            {show && (
              <div 
                ref={dropdownRef}
                className="absolute right-0 mt-2 bg-white shadow-lg rounded-md w-48 py-1 z-10 border border-gray-200"
              >
                <div className="px-4 py-2 border-b border-gray-200">
                  <div className="flex items-center justify-between space-x-2">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{displayName}</p>
                      {isUsingDemoUser && (
                        <p className="text-[11px] text-[#1D5176]">Demo user active</p>
                      )}
                    </div>
                    <img 
                      className="w-10 h-10 rounded-full"
                      src={displayAvatar}
                      alt="User profile"
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        (e.target as HTMLImageElement).src = defaultProfileImage;
                      }}
                    />
                  </div>
                </div>

                <div
                  className="border-b border-gray-200 px-4 py-2"
                  aria-label={apiApplicationLabel}
                  aria-live="polite"
                >
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                    Application
                  </p>
                  <p className="mt-0.5 whitespace-nowrap text-xs font-medium text-[#1D5176]">
                    {apiApplicationInfo === undefined
                      ? 'Loading version...'
                      : apiApplicationInfo === null
                        ? 'API unavailable'
                        : `v${apiApplicationInfo.applicationVersion} · Build ${apiApplicationInfo.buildNumber}`}
                  </p>
                </div>

                {demoModeAvailable && (
                  <div className="px-4 py-3 border-b border-gray-200 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">Demo User</span>
                      {isUsingDemoUser && (
                        <button
                          type="button"
                          onClick={() => clearDemoUserEmail()}
                          className="text-[11px] font-medium text-[#1D5176] hover:underline"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <button
                        type="button"
                        aria-label="Demo user selector"
                        aria-expanded={demoSelectorOpen}
                        onClick={() => {
                          if (!demoUsersLoading) {
                            setDemoSelectorOpen((previous) => !previous);
                          }
                        }}
                        disabled={demoUsersLoading}
                        className="flex w-full items-center justify-between rounded border border-gray-300 bg-white px-2 py-1.5 text-left text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          {selectedDemoUserOption ? (
                            <>
                              <AvatarImage
                                name={selectedDemoUserOption.name}
                                filename={selectedDemoUserOption.avatarUri || null}
                                size={22}
                              />
                              <span className="min-w-0">
                                <span className="block truncate font-medium">{selectedDemoUserOption.name}</span>
                                <span className="block truncate text-[11px] text-gray-500">{selectedDemoUserOption.emailAddress}</span>
                              </span>
                            </>
                          ) : (
                            <span className="truncate">Off</span>
                          )}
                        </span>
                        <svg
                          className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-200 ${demoSelectorOpen ? 'rotate-180' : ''}`}
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M7 10l5 5 5-5z" />
                        </svg>
                      </button>

                      {demoSelectorOpen && (
                        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                          <button
                            type="button"
                            onClick={() => handleDemoUserChange('')}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-[10px] font-semibold text-gray-600">
                              Off
                            </span>
                            <span className="truncate font-medium">Use real account</span>
                          </button>
                          {demoUserOptions.map((option) => {
                            const isSelected = option.emailAddress === demoUserEmail;

                            return (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() => handleDemoUserChange(option.emailAddress)}
                                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${isSelected ? 'bg-[#F2F6F9]' : 'text-gray-700'}`}
                              >
                                <AvatarImage
                                  name={option.name}
                                  filename={option.avatarUri || null}
                                  size={22}
                                />
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate font-medium">{option.name}</span>
                                  <span className="block truncate text-[11px] text-gray-500">{option.emailAddress}</span>
                                </span>
                                {isSelected && (
                                  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#1D5176]">
                                    Active
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500">
                      {demoUsersLoading
                        ? 'Loading demo users...'
                        : demoUsersError || (isUsingDemoUser ? `Impersonating ${demoUserEmail}` : 'Using your real account identity.')}
                    </p>
                  </div>
                )}

                <div className="px-2 py-1 flex flex-col space-y-1">
                  <PageLayout />
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
